import React, { useContext, useEffect, useState } from "react";

// Ícones
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatIcon from '@mui/icons-material/Chat';
import StarIcon from '@mui/icons-material/Star';
import GroupIcon from '@mui/icons-material/Group';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import SortIcon from '@mui/icons-material/Sort';
import QueueIcon from '@mui/icons-material/Queue';
import PlaceIcon from '@mui/icons-material/Place';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BadgeIcon from '@mui/icons-material/Badge';
import DrawIcon from '@mui/icons-material/Draw';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MessageIcon from '@mui/icons-material/Message';
import AddIcon from '@mui/icons-material/Add';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';

// Material UI
import {
	Box,
	CardActionArea,
	CircularProgress,
	FormControl,
	MenuItem,
	TextField,
	Button,
	Card,
	CardMedia,
	Container,
	Menu,
	Paper,
	Select,
	Tooltip,
	Typography,
	Grid,
	Tabs,
	Tab,
	Switch,
	Stack,
	Fade,
	Chip,
	Divider,
	IconButton,
	Collapse,
	alpha,
} from "@mui/material";
import { styled, useTheme } from '@mui/material/styles';

// Outros componentes
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Page from '../../components/Page';
import useSettings from '../../hooks/useSettings';
import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import socket from "../../hooks/useSocket";
import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";
import { AuthContext } from "../../context/Auth/AuthContext";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Imagens (previews do tipo de listagem de setores)
import ButtonsImg from "../../assets/images/Buttons.png";
import DefaultImg from "../../assets/images/Default.png";
import ListImg from "../../assets/images/List.png";

// ---------------------------------------------------------------------------
// Estilos
// ---------------------------------------------------------------------------

const SectionPaper = styled(Paper)(({ theme }) => {
	const isDark = theme.palette.mode === 'dark';
	return {
		borderRadius: 12,
		padding: theme.spacing(3),
		backgroundColor: isDark
			? alpha(theme.palette.background.paper, 0.6)
			: theme.palette.background.paper,
		border: `1px solid ${isDark
			? alpha(theme.palette.common.white, 0.08)
			: alpha(theme.palette.grey[500], 0.16)}`,
		boxShadow: isDark
			? 'none'
			: `0 1px 2px ${alpha(theme.palette.grey[900], 0.04)}`,
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: 150 }),
		'&:hover': {
			borderColor: isDark
				? alpha(theme.palette.common.white, 0.16)
				: alpha(theme.palette.grey[500], 0.32),
		},
	};
});

const SectionHeader = ({ icon: Icon, title, description, action }) => (
	<Box sx={{ mb: 2 }}>
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
				{Icon && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: 36,
							height: 36,
							borderRadius: 1.5,
							bgcolor: (t) => alpha(
								t.palette.primary.main,
								t.palette.mode === 'dark' ? 0.18 : 0.12
							),
							color: (t) => t.palette.mode === 'dark'
								? t.palette.primary.light
								: t.palette.primary.main,
							flexShrink: 0,
						}}
					>
						<Icon fontSize="small" />
					</Box>
				)}
				<Box sx={{ minWidth: 0 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
						{title}
					</Typography>
					{description && (
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
							{description}
						</Typography>
					)}
				</Box>
			</Box>
			{action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
		</Box>
	</Box>
);

const TabPanel = ({ value, index, children }) => (
	<Fade in={value === index} timeout={250}>
		<div style={{ display: value === index ? 'block' : 'none' }} role="tabpanel">
			{children}
		</div>
	</Fade>
);

const PageHeader = ({ title, description }) => (
	<Box sx={{ mb: 1 }}>
		<Typography variant="h6" sx={{ fontWeight: 600 }}>
			{title}
		</Typography>
		{description && (
			<Typography variant="body2" color="text.secondary">
				{description}
			</Typography>
		)}
	</Box>
);

const PreviewCard = styled(Card)(() => ({
	overflow: 'visible',
	boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
}));

// ---------------------------------------------------------------------------
// Conversão ticketTime (backend usa "HH:MM" -> subHours/subMinutes)
// ---------------------------------------------------------------------------

const ticketTimeFromHHMM = (raw) => {
	if (!raw || typeof raw !== 'string' || !raw.includes(':')) {
		return { value: '', unit: 'hours' };
	}
	const [h, m] = raw.split(':').map((n) => parseInt(n, 10) || 0);
	const totalMinutes = h * 60 + m;
	if (totalMinutes === 0) return { value: 0, unit: 'hours' };
	if (totalMinutes % 1440 === 0) return { value: totalMinutes / 1440, unit: 'days' };
	if (totalMinutes % 60 === 0) return { value: totalMinutes / 60, unit: 'hours' };
	return { value: totalMinutes, unit: 'minutes' };
};

const ticketTimeToTotalMinutes = (value, unit) => {
	const n = parseInt(value, 10);
	if (isNaN(n) || n < 0) return null;
	if (unit === 'days') return n * 1440;
	if (unit === 'hours') return n * 60;
	return n;
};

const ticketTimeToHHMM = (value, unit) => {
	const totalMinutes = ticketTimeToTotalMinutes(value, unit);
	if (totalMinutes === null) return null;
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const pluralize = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;

const safeParseArray = (raw) => {
	if (Array.isArray(raw)) return raw;
	if (typeof raw !== 'string' || raw.trim() === '') return [];
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

const humanizeTicketTime = (value, unit) => {
	const totalMinutes = ticketTimeToTotalMinutes(value, unit);
	if (totalMinutes === null) return null;
	if (totalMinutes === 0) return 'Sem tempo mínimo';
	const days = Math.floor(totalMinutes / 1440);
	const hours = Math.floor((totalMinutes % 1440) / 60);
	const minutes = totalMinutes % 60;
	const parts = [];
	if (days) parts.push(pluralize(days, 'dia', 'dias'));
	if (hours) parts.push(pluralize(hours, 'hora', 'horas'));
	if (minutes) parts.push(pluralize(minutes, 'minuto', 'minutos'));
	if (parts.length === 1) return parts[0];
	if (parts.length === 2) return `${parts[0]} e ${parts[1]}`;
	return `${parts[0]}, ${parts[1]} e ${parts[2]}`;
};

// Alerta de fila — converte ms ↔ {value, unit}. A unidade base no DB é ms
// (mais fácil pra cálculos no backend).
const QUEUE_ALERT_DEFAULT_MS = 15 * 60 * 1000;
// Intervalo de checagem: SEMPRE em minutos. Mínimo 5, máximo 60.
const QUEUE_INTERVAL_MIN_MIN = 5;
const QUEUE_INTERVAL_MAX_MIN = 60;
const QUEUE_INTERVAL_MIN_MS = QUEUE_INTERVAL_MIN_MIN * 60 * 1000;
const QUEUE_INTERVAL_MAX_MS = QUEUE_INTERVAL_MAX_MIN * 60 * 1000;
const QUEUE_INTERVAL_DEFAULT_MS = QUEUE_INTERVAL_MIN_MS;

const queueAlertFromMs = (ms, fallbackMs = QUEUE_ALERT_DEFAULT_MS) => {
	const safe = typeof ms === 'number' && ms > 0 ? ms : fallbackMs;
	const totalMinutes = Math.round(safe / 60000);
	if (totalMinutes >= 60 && totalMinutes % 60 === 0) {
		return { value: totalMinutes / 60, unit: 'hours' };
	}
	return { value: totalMinutes, unit: 'minutes' };
};

const queueAlertToMs = (value, unit) => {
	const n = parseInt(value, 10);
	if (isNaN(n) || n <= 0) return null;
	return unit === 'hours' ? n * 60 * 60 * 1000 : n * 60 * 1000;
};

const humanizeQueueAlert = (value, unit) => {
	const ms = queueAlertToMs(value, unit);
	if (ms === null) return null;
	const totalMinutes = Math.round(ms / 60000);
	if (totalMinutes < 60) return pluralize(totalMinutes, 'minuto', 'minutos');
	const hours = Math.floor(totalMinutes / 60);
	const mins = totalMinutes % 60;
	if (mins === 0) return pluralize(hours, 'hora', 'horas');
	return `${pluralize(hours, 'hora', 'horas')} e ${pluralize(mins, 'minuto', 'minutos')}`;
};

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

const Settings = () => {
	const { themeStretch } = useSettings();
	const theme = useTheme();
	const { user } = useContext(AuthContext);
	const companyId = user?.companyId;

	const [settings, setSettings] = useState({});
	const [ticketTimeValue, setTicketTimeValue] = useState("");
	const [ticketTimeUnit, setTicketTimeUnit] = useState("hours");
	const [messageRating, setMessageRating] = useState("");
	// Alerta de fila: o backend armazena em ms; a UI usa min/h por usabilidade.
	const [queueAlertValue, setQueueAlertValue] = useState("");
	const [queueAlertUnit, setQueueAlertUnit] = useState("minutes");
	// Intervalo entre checagens (sempre em minutos, 5–60)
	const [queueIntervalValue, setQueueIntervalValue] = useState("");
	const [periodOfWork, setPeriodOfWork] = useState({
		morning: { start: '00:00', end: '00:00' },
		afternoon: { start: '00:00', end: '00:00' },
		night: { start: '00:00', end: '00:00' },
	});

	// Palavras-chave (recently-closed keywords/responses)
	const [keywordList, setKeywordList] = useState([]);
	const [responseList, setResponseList] = useState([]);
	const [newKeyword, setNewKeyword] = useState("");
	const [newResponse, setNewResponse] = useState("");
	const [keywordsLoading, setKeywordsLoading] = useState(false);
	const [keywordsSaving, setKeywordsSaving] = useState(false);

	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState(0);

	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const handleClick = (event) => setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);

	// Previews do menu de tipo de listagem
	const PreviewDefault = () => (
		<PreviewCard>
			<CardActionArea>
				<CardMedia component="img" height="150" image={DefaultImg} alt="Padrão" />
			</CardActionArea>
		</PreviewCard>
	);
	const PreviewList = () => (
		<PreviewCard>
			<CardActionArea>
				<CardMedia component="img" height="200" image={ListImg} alt="Lista" />
			</CardActionArea>
		</PreviewCard>
	);
	const PreviewButtons = () => (
		<PreviewCard>
			<CardActionArea>
				<CardMedia component="img" height="150" image={ButtonsImg} alt="Botões" />
			</CardActionArea>
		</PreviewCard>
	);

	// -------------------------------------------------------------------------
	// Carregamento
	// -------------------------------------------------------------------------
	useEffect(() => {
		const fetchSession = async () => {
			setLoading(true);
			try {
				const { data } = await api.get("/settings-company");
				if (data?.findPeriodOfWork) {
					setPeriodOfWork({
						morning: JSON.parse(data.findPeriodOfWork.morning),
						afternoon: JSON.parse(data.findPeriodOfWork.afternoon),
						night: JSON.parse(data.findPeriodOfWork.night),
					});
				}
				setMessageRating(data?.messageRating ?? "");
				const parsed = ticketTimeFromHHMM(data?.ticketTime);
				setTicketTimeValue(parsed.value === '' ? '' : String(parsed.value));
				setTicketTimeUnit(parsed.unit);
				const parsedAlert = queueAlertFromMs(data?.queueAlertThresholdMs);
				setQueueAlertValue(String(parsedAlert.value));
				setQueueAlertUnit(parsedAlert.unit);
				// Intervalo é sempre exibido em minutos (5-60). Se vier do banco em ms,
				// converte e clampa pro range permitido.
				const intervalMs =
					typeof data?.queueAlertIntervalMs === 'number' && data.queueAlertIntervalMs > 0
						? data.queueAlertIntervalMs
						: QUEUE_INTERVAL_DEFAULT_MS;
				const intervalMin = Math.min(
					QUEUE_INTERVAL_MAX_MIN,
					Math.max(QUEUE_INTERVAL_MIN_MIN, Math.round(intervalMs / 60000))
				);
				setQueueIntervalValue(String(intervalMin));
				setSettings(data ?? {});
			} catch (err) {
				ToastError(err);
			} finally {
				setLoading(false);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		socket.on("settings", (data) => {
			if (data.action === "update") {
				setLoading(true);
				setSettings(data.setting);
				setTimeout(() => setLoading(false), 100);
			}
		});
		return () => {
			socket.off("settings");
		};
	}, []);

	useEffect(() => {
		if (!settings?.useKeywords) return;
		let cancelled = false;
		(async () => {
			setKeywordsLoading(true);
			try {
				const { data } = await api.get("/keywords");
				const k = data?.keywords ?? {};
				if (cancelled) return;
				setKeywordList(safeParseArray(k.recentlyClosedKeywords));
				setResponseList(safeParseArray(k.recentlyClosedMessage));
			} catch (err) {
				if (!cancelled) ToastError(err);
			} finally {
				if (!cancelled) setKeywordsLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [settings?.useKeywords]);

	// -------------------------------------------------------------------------
	// Handlers
	// -------------------------------------------------------------------------
	const handleChangeSetting = async (e) => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;
		try {
			const { data } = await api.put(`/settings/${settingKey}`, { value: selectedValue });
			ToastSuccess(i18n.t("settings.success"));
			if (settingKey === "serviceRating") {
				setMessageRating(data?.messageRating ?? "");
			}
		} catch (err) {
			ToastError(err);
		}
	};

	const handleChangeTicketTime = async () => {
		const hhmm = ticketTimeToHHMM(ticketTimeValue, ticketTimeUnit);
		if (hhmm === null) {
			ToastError("Informe um valor válido (número inteiro maior ou igual a zero).");
			return;
		}
		try {
			await api.put(`/settings/ticketTime`, { value: hhmm });
			ToastSuccess(i18n.t("settings.success"));
		} catch (err) {
			ToastError(err);
		}
	};

	const handleChangeMessageRating = async () => {
		try {
			await api.put(`/settings/messageRating`, { value: messageRating });
			ToastSuccess(i18n.t("settings.success"));
		} catch (err) {
			ToastError(err);
		}
	};

	const handleChangeQueueAlertThreshold = async () => {
		const ms = queueAlertToMs(queueAlertValue, queueAlertUnit);
		if (ms === null) {
			ToastError("Informe um valor inteiro maior que zero.");
			return;
		}
		try {
			await api.put(`/settings/queueAlertThresholdMs`, { value: ms });
			setSettings((prev) => ({ ...prev, queueAlertThresholdMs: ms }));
			ToastSuccess(i18n.t("settings.success"));
		} catch (err) {
			ToastError(err);
		}
	};

	const handleChangeQueueAlertInterval = async () => {
		const minutes = parseInt(queueIntervalValue, 10);
		if (Number.isNaN(minutes) || minutes < QUEUE_INTERVAL_MIN_MIN) {
			ToastError(`O intervalo mínimo é de ${QUEUE_INTERVAL_MIN_MIN} minutos.`);
			return;
		}
		if (minutes > QUEUE_INTERVAL_MAX_MIN) {
			ToastError(`O intervalo máximo é de ${QUEUE_INTERVAL_MAX_MIN} minutos.`);
			return;
		}
		const ms = minutes * 60 * 1000;
		try {
			await api.put(`/settings/queueAlertIntervalMs`, { value: ms });
			setSettings((prev) => ({ ...prev, queueAlertIntervalMs: ms }));
			ToastSuccess(i18n.t("settings.success"));
		} catch (err) {
			ToastError(err);
		}
	};

	const handleChangePeriodOfWork = async () => {
		try {
			await api.put(`/settings/periodOfWork`, { value: periodOfWork });
			ToastSuccess(i18n.t("settings.success"));
		} catch (err) {
			ToastError(err);
		}
	};

	const selectOptionInContent = async (type) => {
		await handleChangeSetting({ target: { value: type, name: "typeMessage" } });
		handleClose();
	};

	const handleSwitchChange = (name) => (e) =>
		handleChangeSetting({ target: { name, value: e.target.checked } });

	const updatePeriod = (period, key) => (e) => {
		setPeriodOfWork((prev) => ({
			...prev,
			[period]: { ...prev[period], [key]: e.target.value },
		}));
	};

	const handleTabChange = (_event, newValue) => setActiveTab(newValue);

	// Palavras-chave
	const addKeyword = () => {
		const v = newKeyword.trim().toLowerCase();
		if (!v) return;
		if (keywordList.includes(v)) {
			ToastError('Esta palavra já foi adicionada.');
			setNewKeyword("");
			return;
		}
		setKeywordList((prev) => [...prev, v]);
		setNewKeyword("");
	};

	const removeKeyword = (index) =>
		setKeywordList((prev) => prev.filter((_, i) => i !== index));

	const addResponse = () => {
		const v = newResponse.trim();
		if (!v) return;
		if (responseList.includes(v)) {
			ToastError('Esta resposta já foi adicionada.');
			setNewResponse("");
			return;
		}
		setResponseList((prev) => [...prev, v]);
		setNewResponse("");
	};

	const removeResponse = (index) =>
		setResponseList((prev) => prev.filter((_, i) => i !== index));

	const saveKeywords = async () => {
		if (!companyId) return;
		setKeywordsSaving(true);
		try {
			await api.put(`/keywords/${companyId}`, {
				recentlyClosedKeywords: JSON.stringify(keywordList),
				recentlyClosedMessage: JSON.stringify(responseList),
			});
			ToastSuccess(i18n.t("settings.success"));
		} catch (err) {
			ToastError(err);
		} finally {
			setKeywordsSaving(false);
		}
	};

	// -------------------------------------------------------------------------
	// Primitivas de UI
	// -------------------------------------------------------------------------
	const renderLoading = () => (
		<Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
			<CircularProgress size={20} />
		</Box>
	);

	const SwitchSetting = ({ icon, title, description, name, checked, tooltip }) => (
		<SectionPaper>
			<SectionHeader
				icon={icon}
				title={
					<Stack direction="row" alignItems="center" spacing={0.75}>
						<span>{title}</span>
						{tooltip && (
							<Tooltip title={tooltip} arrow>
								<InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
							</Tooltip>
						)}
					</Stack>
				}
				description={description}
				action={
					loading ? (
						<CircularProgress size={18} />
					) : (
						<Switch
							checked={Boolean(checked)}
							onChange={handleSwitchChange(name)}
							color="primary"
						/>
					)
				}
			/>
		</SectionPaper>
	);

	const SelectSetting = ({ icon, title, description, name, value, options, tooltip }) => (
		<SectionPaper>
			<SectionHeader
				icon={icon}
				title={
					<Stack direction="row" alignItems="center" spacing={0.75}>
						<span>{title}</span>
						{tooltip && (
							<Tooltip title={tooltip} arrow>
								<InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
							</Tooltip>
						)}
					</Stack>
				}
				description={description}
			/>
			{loading ? (
				renderLoading()
			) : (
				<FormControl fullWidth size="small">
					<Select
						name={name}
						value={value ?? ''}
						onChange={handleChangeSetting}
						sx={{ borderRadius: 1.5 }}
					>
						{options.map((opt) => (
							<MenuItem key={String(opt.value)} value={opt.value}>
								{opt.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			)}
		</SectionPaper>
	);

	const PeriodBlock = ({ icon: Icon, label, period }) => (
		<SectionPaper>
			<Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
				<Box
					sx={{
						width: 36,
						height: 36,
						borderRadius: 1.5,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						bgcolor: alpha(
							theme.palette.primary.main,
							theme.palette.mode === 'dark' ? 0.18 : 0.12
						),
						color: theme.palette.mode === 'dark'
							? theme.palette.primary.light
							: theme.palette.primary.main,
					}}
				>
					<Icon fontSize="small" />
				</Box>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					{label}
				</Typography>
			</Stack>

			<Stack spacing={1.5}>
				<TextField
					label={i18n.t("settings.settings.periodTime.start")}
					type="time"
					value={periodOfWork[period]?.start ?? '00:00'}
					onChange={updatePeriod(period, 'start')}
					InputLabelProps={{ shrink: true }}
					inputProps={{ step: 300 }}
					size="small"
					fullWidth
				/>
				<TextField
					label={i18n.t("settings.settings.periodTime.end")}
					type="time"
					value={periodOfWork[period]?.end ?? '00:00'}
					onChange={updatePeriod(period, 'end')}
					InputLabelProps={{ shrink: true }}
					inputProps={{ step: 300 }}
					size="small"
					fullWidth
				/>
			</Stack>
		</SectionPaper>
	);

	// -------------------------------------------------------------------------
	// Conteúdo dos tabs
	// -------------------------------------------------------------------------
	const renderTypeQueue = () => (
		<SectionPaper>
			<SectionHeader
				icon={DashboardCustomizeIcon}
				title={i18n.t("settings.settings.typeQueue.name")}
				description="Defina como os setores aparecem para o cliente ao abrir um atendimento."
			/>
			{loading ? (
				renderLoading()
			) : (
				<>
					<Button
						variant="outlined"
						startIcon={<TuneIcon />}
						onClick={handleClick}
						sx={{ justifyContent: 'space-between', borderRadius: 1.5, textTransform: 'none' }}
						fullWidth
					>
						{i18n.t(`settings.settings.typeQueue.options.${settings?.typeMessage ?? 'default'}`)}
					</Button>
					<Menu
						anchorEl={anchorEl}
						open={open}
						onClose={handleClose}
						slotProps={{
							paper: {
								sx: {
									p: 1,
									width: 320,
									borderRadius: 2,
									'& .MuiMenuItem-root': {
										px: 1,
										py: 1.5,
										borderRadius: 1,
										my: 0.25,
									},
								},
							},
						}}
					>
						<Typography variant="caption" sx={{ px: 1.5, py: 0.5, fontWeight: 600, color: 'text.secondary' }}>
							Selecione o tipo de visualização
						</Typography>

						<MenuItem onClick={() => selectOptionInContent('default')}>
							<Tooltip title={<PreviewDefault />} placement="right" arrow>
								<FormatListBulletedIcon color="primary" sx={{ mr: 2 }} />
							</Tooltip>
							{i18n.t(`settings.settings.typeQueue.options.default`)}
						</MenuItem>

						<MenuItem disabled onClick={() => selectOptionInContent('list')}>
							<Tooltip title={<PreviewList />} placement="right" arrow>
								<FormatListBulletedIcon color="action" sx={{ mr: 2 }} />
							</Tooltip>
							{i18n.t(`settings.settings.typeQueue.options.list`)}
						</MenuItem>

						<MenuItem disabled onClick={() => selectOptionInContent('buttons')}>
							<Tooltip title={<PreviewButtons />} placement="right" arrow>
								<DashboardCustomizeIcon color="action" sx={{ mr: 2 }} />
							</Tooltip>
							{i18n.t(`settings.settings.typeQueue.options.buttons`)}
						</MenuItem>
					</Menu>
				</>
			)}
		</SectionPaper>
	);

	return (
		<Page title={i18n.t("settings.title")}>
			<Container maxWidth={themeStretch ? false : "lg"}>
				<HeaderBreadcrumbs heading={i18n.t("settings.title")} />

				<Paper
					variant="outlined"
					sx={{
						borderRadius: 2,
						mb: 3,
						bgcolor: 'background.paper',
					}}
				>
					<Tabs
						value={activeTab}
						onChange={handleTabChange}
						variant="scrollable"
						scrollButtons="auto"
						sx={{
							px: 1,
							'& .MuiTab-root': {
								minHeight: 56,
								textTransform: 'none',
								fontWeight: 500,
								fontSize: 14,
							},
							'& .Mui-selected': { fontWeight: 600 },
						}}
					>
						<Tab icon={<TuneIcon />} iconPosition="start" label="Fluxo de tickets" />
						<Tab icon={<ChatIcon />} iconPosition="start" label="Mensagens automáticas" />
						<Tab icon={<AccessTimeIcon />} iconPosition="start" label="Horário de expediente" />
						<Tab icon={<StarIcon />} iconPosition="start" label="Pesquisa de satisfação" />
					</Tabs>
				</Paper>

				{/* ---------------- TAB 0: ATENDIMENTO ---------------- */}
				<TabPanel value={activeTab} index={0}>
					<PageHeader
						title="Fluxo de tickets"
						description="Como os tickets são organizados, distribuídos e exibidos para a sua equipe."
					/>

					<Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3, mb: 1.5, fontWeight: 600 }}>
						Visualização e ordenação
					</Typography>

					<Grid container spacing={2.5} alignItems="stretch">
						<Grid item xs={12} md={6}>
							{renderTypeQueue()}
						</Grid>

						<Grid item xs={12} md={6}>
							<SelectSetting
								icon={SortIcon}
								title={i18n.t("settings.settings.ticketsOrder.name")}
								description="Critério usado para ordenar a lista de tickets."
								name="ticketsOrder"
								value={settings?.ticketsOrder}
								options={[
									{ value: 'default', label: i18n.t("settings.settings.ticketsOrder.options.default") },
									{ value: 'desc', label: i18n.t("settings.settings.ticketsOrder.options.desc") },
									{ value: 'asc', label: i18n.t("settings.settings.ticketsOrder.options.asc") },
								]}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<SelectSetting
								icon={QueueIcon}
								title={i18n.t("settings.settings.pendingTicketsLogic.name")}
								description="Como os tickets aguardando são distribuídos aos atendentes."
								tooltip="No modo Próximo da fila, o atendente recebe automaticamente o ticket que está há mais tempo aguardando, sem ver a lista de pendentes."
								name="pendingTicketsLogic"
								value={settings?.pendingTicketsLogic || 'list'}
								options={[
									{ value: 'list', label: i18n.t("settings.settings.pendingTicketsLogic.options.list") },
									{ value: 'next', label: i18n.t("settings.settings.pendingTicketsLogic.options.next") },
								]}
							/>
						</Grid>
					</Grid>

					<Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 4, mb: 1.5, fontWeight: 600 }}>
						Comportamento
					</Typography>

					<Grid container spacing={2.5} alignItems="stretch">
						<Grid item xs={12} md={6}>
							<SwitchSetting
								icon={GroupIcon}
								title={i18n.t("settings.settings.answerInGroups.name")}
								description="Permite que atendentes respondam mensagens vindas de grupos."
								name="answerInGroups"
								checked={settings?.answerInGroups}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<SwitchSetting
								icon={SmartToyIcon}
								title="Ignorar chatbot por padrão"
								description="Novos contatos não passam pelo fluxo automático do chatbot."
								tooltip="Ao habilitar, todos os novos contatos da plataforma serão adicionados à lista de ignorar chatbot."
								name="ignoreChatbot"
								checked={settings?.ignoreChatbot}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<SwitchSetting
								icon={PlaceIcon}
								title="Atendimento presencial"
								description="Habilita a opção de marcar atendimentos como presenciais."
								name="usePresence"
								checked={settings?.usePresence}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<SwitchSetting
								icon={BadgeIcon}
								title="Exibir cargo na mensagem"
								description="Mostra o cargo do atendente junto da assinatura nas mensagens."
								name="usePosition"
								checked={settings?.usePosition}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<SwitchSetting
								icon={VisibilityIcon}
								title="Atendente escolhe assinar"
								description="O atendente decide manualmente se sua assinatura aparece em cada mensagem."
								tooltip="Ao habilitar, os usuários podem escolher enviar ou não seu nome assinado nas mensagens."
								name="viewSignature"
								checked={settings?.viewSignature}
							/>
						</Grid>

						{!settings?.viewSignature && (
							<Grid item xs={12} md={6}>
								<SwitchSetting
									icon={DrawIcon}
									title="Assinatura automática"
									description="Adiciona automaticamente o nome do atendente em todas as mensagens."
									tooltip="Ao habilitar, as assinaturas com nome do atendente serão exibidas nas mensagens."
									name="useSignature"
									checked={settings?.useSignature}
								/>
							</Grid>
						)}
					</Grid>
				</TabPanel>

				{/* ---------------- TAB 1: MENSAGENS ---------------- */}
				<TabPanel value={activeTab} index={1}>
					<PageHeader
						title="Mensagens automáticas"
						description="Tempo mínimo entre tickets e respostas automáticas após o atendimento."
					/>

					<Stack spacing={2.5} sx={{ mt: 0 }}>
						<Box>
							<SectionPaper>
								<SectionHeader
									icon={AccessTimeIcon}
									title={
										<Stack direction="row" alignItems="center" spacing={0.75}>
											<span>{i18n.t("settings.settings.ticketTime.name")}</span>
											<Tooltip title={i18n.t("settings.settings.ticketTime.obs")} arrow>
												<InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
											</Tooltip>
										</Stack>
									}
									description="Tempo mínimo entre o fechamento e a abertura de um novo ticket do mesmo contato."
								/>
								{loading ? (
									renderLoading()
								) : (
									<>
										<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
											<TextField
												size="small"
												name="ticketTime"
												label="Tempo"
												type="number"
												value={ticketTimeValue}
												onChange={(e) => {
													const v = e.target.value;
													if (v === '' || /^\d+$/.test(v)) setTicketTimeValue(v);
												}}
												InputLabelProps={{ shrink: true }}
												inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
												sx={{ flex: 1 }}
											/>
											<FormControl size="small" sx={{ minWidth: 140 }}>
												<Select
													value={ticketTimeUnit}
													onChange={(e) => setTicketTimeUnit(e.target.value)}
													sx={{ borderRadius: 1.5 }}
												>
													<MenuItem value="minutes">Minutos</MenuItem>
													<MenuItem value="hours">Horas</MenuItem>
													<MenuItem value="days">Dias</MenuItem>
												</Select>
											</FormControl>
											<Button
												variant="contained"
												color="primary"
												startIcon={<SaveIcon />}
												onClick={handleChangeTicketTime}
												sx={{ borderRadius: 1.5, boxShadow: 'none', whiteSpace: 'nowrap' }}
											>
												Salvar
											</Button>
										</Stack>
										{ticketTimeValue !== '' && humanizeTicketTime(ticketTimeValue, ticketTimeUnit) && (
											<Box
												sx={{
													mt: 2,
													px: 1.75,
													py: 1.25,
													borderRadius: 1.5,
													display: 'flex',
													alignItems: 'center',
													gap: 1.25,
													bgcolor: (t) => alpha(
														ticketTimeToTotalMinutes(ticketTimeValue, ticketTimeUnit) === 0
															? t.palette.warning.main
															: t.palette.info.main,
														0.08
													),
													border: (t) => `1px solid ${alpha(
														ticketTimeToTotalMinutes(ticketTimeValue, ticketTimeUnit) === 0
															? t.palette.warning.main
															: t.palette.info.main,
														0.2
													)}`,
												}}
											>
												<AccessTimeIcon
													sx={{
														fontSize: 18,
														color: ticketTimeToTotalMinutes(ticketTimeValue, ticketTimeUnit) === 0
															? 'warning.main'
															: 'info.main',
													}}
												/>
												<Box sx={{ minWidth: 0 }}>
													<Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
														Tempo total
													</Typography>
													<Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
														{humanizeTicketTime(ticketTimeValue, ticketTimeUnit)}
														{ticketTimeToTotalMinutes(ticketTimeValue, ticketTimeUnit) === 0 && (
															<Typography component="span" variant="caption" sx={{ ml: 0.75, fontWeight: 400, color: 'text.secondary' }}>
																— novos tickets serão criados imediatamente
															</Typography>
														)}
													</Typography>
												</Box>
											</Box>
										)}
									</>
								)}
							</SectionPaper>
						</Box>

						<Box>
							<SwitchSetting
								icon={KeyboardIcon}
								title={i18n.t("settings.settings.keywords.name")}
								description="Reaproveita tickets recém-fechados quando o cliente envia palavras-chave (ex.: 'obrigado')."
								tooltip={i18n.t("settings.settings.keywords.obs")}
								name="useKeywords"
								checked={settings?.useKeywords}
							/>

							<Collapse in={Boolean(settings?.useKeywords)} timeout={300} unmountOnExit>
								<Box sx={{ mt: 2 }}>
									<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
										<Button
											variant="contained"
											color="primary"
											startIcon={keywordsSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
											onClick={saveKeywords}
											disabled={keywordsSaving || keywordsLoading}
											sx={{ borderRadius: 1.5, boxShadow: 'none', whiteSpace: 'nowrap' }}
										>
											Salvar palavras-chave
										</Button>
									</Box>

							<Grid container spacing={2.5} alignItems="stretch">
								<Grid item xs={12} md={6}>
									<SectionPaper>
										<SectionHeader
											icon={PsychologyIcon}
											title="Palavras-chave de ativação"
											description="Termos que disparam a resposta automática quando o cliente os envia."
										/>

										<Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
											<TextField
												size="small"
												fullWidth
												placeholder="Ex.: obrigado, valeu, thanks…"
												value={newKeyword}
												onChange={(e) => setNewKeyword(e.target.value)}
												onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
												disabled={keywordsLoading}
											/>
											<Button
												variant="outlined"
												startIcon={<AddIcon />}
												onClick={addKeyword}
												disabled={keywordsLoading || !newKeyword.trim()}
												sx={{ borderRadius: 1.5, whiteSpace: 'nowrap' }}
											>
												Adicionar
											</Button>
										</Stack>

										<Divider sx={{ my: 1.5 }} />

										<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
											{keywordList.length} {keywordList.length === 1 ? 'palavra configurada' : 'palavras configuradas'}
										</Typography>

										<Box
											sx={{
												flex: 1,
												minHeight: 110,
												p: 1.5,
												borderRadius: 1.5,
												border: (t) => `1px dashed ${alpha(t.palette.divider, 0.6)}`,
												bgcolor: (t) =>
													t.palette.mode === 'dark'
														? alpha(t.palette.background.paper, 0.4)
														: alpha(t.palette.primary.main, 0.03),
												transition: 'border-color 0.15s ease',
												'&:hover': {
													borderColor: (t) => alpha(t.palette.primary.main, 0.35),
												},
											}}
										>
											{keywordsLoading ? (
												<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
													<CircularProgress size={20} />
												</Box>
											) : keywordList.length === 0 ? (
												<Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 3 }}>
													Nenhuma palavra adicionada ainda.
												</Typography>
											) : (
												<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
													{keywordList.map((kw, i) => (
														<Chip
															key={`${kw}-${i}`}
															label={kw}
															onDelete={() => removeKeyword(i)}
															size="small"
															sx={{
																borderRadius: 1,
																bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
																color: 'primary.main',
																border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
																fontWeight: 500,
																'& .MuiChip-deleteIcon': {
																	color: (t) => alpha(t.palette.primary.main, 0.5),
																	'&:hover': { color: 'primary.main' },
																},
															}}
														/>
													))}
												</Box>
											)}
										</Box>
									</SectionPaper>
								</Grid>

								<Grid item xs={12} md={6}>
									<SectionPaper>
										<SectionHeader
											icon={ChatBubbleIcon}
											title="Respostas automáticas"
											description="Uma mensagem aleatória da lista é enviada quando a palavra-chave é detectada."
										/>

										<Stack spacing={1} sx={{ mb: 1.5 }}>
											<TextField
												size="small"
												multiline
												rows={3}
												fullWidth
												placeholder="Ex.: De nada! Se precisar de mais alguma coisa, estaremos por aqui."
												value={newResponse}
												onChange={(e) => setNewResponse(e.target.value)}
												disabled={keywordsLoading}
											/>
											<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
												<Button
													variant="outlined"
													startIcon={<AddIcon />}
													onClick={addResponse}
													disabled={keywordsLoading || !newResponse.trim()}
													sx={{ borderRadius: 1.5 }}
												>
													Adicionar resposta
												</Button>
											</Box>
										</Stack>

										<Divider sx={{ my: 1.5 }} />

										<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
											{responseList.length} {responseList.length === 1 ? 'resposta configurada' : 'respostas configuradas'}
										</Typography>

										<Box
											sx={{
												flex: 1,
												minHeight: 110,
												p: 1,
												borderRadius: 1.5,
												border: (t) => `1px dashed ${alpha(t.palette.divider, 0.6)}`,
												bgcolor: (t) =>
													t.palette.mode === 'dark'
														? alpha(t.palette.background.paper, 0.4)
														: alpha(t.palette.primary.main, 0.03),
												transition: 'border-color 0.15s ease',
												'&:hover': {
													borderColor: (t) => alpha(t.palette.primary.main, 0.35),
												},
											}}
										>
											{keywordsLoading ? (
												<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
													<CircularProgress size={20} />
												</Box>
											) : responseList.length === 0 ? (
												<Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 3 }}>
													Nenhuma resposta adicionada ainda.
												</Typography>
											) : (
												<Stack spacing={0.75}>
													{responseList.map((msg, i) => (
														<Box
															key={`${i}-${msg.slice(0, 20)}`}
															sx={{
																display: 'flex',
																alignItems: 'flex-start',
																gap: 1,
																px: 1.25,
																py: 0.75,
																borderRadius: 1,
																bgcolor: (t) =>
																	t.palette.mode === 'dark'
																		? alpha(t.palette.background.paper, 0.6)
																		: t.palette.background.paper,
																border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
																transition: 'border-color 0.15s ease, background-color 0.15s ease',
																'&:hover': {
																	borderColor: (t) => alpha(t.palette.primary.main, 0.3),
																	bgcolor: (t) =>
																		t.palette.mode === 'dark'
																			? alpha(t.palette.primary.main, 0.05)
																			: alpha(t.palette.primary.main, 0.02),
																},
															}}
														>
															<Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
																{msg}
															</Typography>
															<Tooltip title="Remover resposta" arrow>
																<IconButton size="small" onClick={() => removeResponse(i)} sx={{ flexShrink: 0 }}>
																	<DeleteOutlineIcon fontSize="small" />
																</IconButton>
															</Tooltip>
														</Box>
													))}
												</Stack>
											)}
										</Box>
									</SectionPaper>
								</Grid>
							</Grid>
						</Box>
					</Collapse>
						</Box>

						{/* Alerta da fila de pendentes — gated por companyId === 2.
							Configura o tempo (em minutos/horas) que define quando o
							backend dispara o aviso no grupo do WhatsApp. */}
						{+user?.companyId === 2 && (
							<Box>
								<SectionPaper>
									<SectionHeader
										icon={WarningAmberIcon}
										title={
											<Stack direction="row" alignItems="center" spacing={0.75}>
												<span>Alerta da fila de pendentes</span>
												<Tooltip
													arrow
													title="A cada 5 minutos, o sistema verifica a fila Suporte e envia um aviso no grupo do WhatsApp caso existam tickets esperando há mais do que esse limite."
												>
													<InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
												</Tooltip>
											</Stack>
										}
										description="Tempo de espera a partir do qual o ticket entra no aviso enviado ao grupo. Silenciado entre 23h e 8h (horário SP)."
									/>

									{loading ? (
										renderLoading()
									) : (
										<>
											{/* Grid de 3 colunas FIXAS: [input flex] [unidade 140px] [botão auto].
											    Mesmas colunas nas duas linhas → alinhamento perfeito por coluna,
											    independente do que o Select renderiza internamente. No mobile (xs)
											    vira 1 coluna empilhada. */}
											<Box
												sx={{
													display: 'grid',
													gridTemplateColumns: { xs: '1fr', sm: '1fr 140px max-content' },
													gap: 1.5,
													alignItems: 'center',
												}}
											>
												<TextField
													size="small"
													name="queueAlertValue"
													label="Tempo limite"
													type="number"
													value={queueAlertValue}
													onChange={(e) => {
														const v = e.target.value;
														if (v === '' || /^\d+$/.test(v)) setQueueAlertValue(v);
													}}
													InputLabelProps={{ shrink: true }}
													inputProps={{ min: 1, step: 1, inputMode: 'numeric' }}
												/>
												<FormControl size="small" fullWidth>
													<Select
														value={queueAlertUnit}
														onChange={(e) => setQueueAlertUnit(e.target.value)}
														sx={{ borderRadius: 1.5 }}
													>
														<MenuItem value="minutes">Minutos</MenuItem>
														<MenuItem value="hours">Horas</MenuItem>
													</Select>
												</FormControl>
												<Button
													variant="contained"
													color="primary"
													startIcon={<SaveIcon />}
													onClick={handleChangeQueueAlertThreshold}
													sx={{ borderRadius: 1.5, boxShadow: 'none', whiteSpace: 'nowrap' }}
												>
													Salvar
												</Button>
											</Box>

											<Typography variant="caption" sx={{ display: 'block', mt: 2.5, mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
												Frequência de verificação da fila (em minutos, entre {QUEUE_INTERVAL_MIN_MIN} e {QUEUE_INTERVAL_MAX_MIN})
											</Typography>
											<Box
												sx={{
													display: 'grid',
													gridTemplateColumns: { xs: '1fr', sm: '1fr 140px max-content' },
													gap: 1.5,
													alignItems: 'center',
												}}
											>
												<TextField
													size="small"
													name="queueIntervalValue"
													label="Intervalo (minutos)"
													type="number"
													value={queueIntervalValue}
													onChange={(e) => {
														const v = e.target.value;
														if (v === '' || /^\d+$/.test(v)) setQueueIntervalValue(v);
													}}
													InputLabelProps={{ shrink: true }}
													inputProps={{
														min: QUEUE_INTERVAL_MIN_MIN,
														max: QUEUE_INTERVAL_MAX_MIN,
														step: 1,
														inputMode: 'numeric',
													}}
													error={(() => {
														const n = parseInt(queueIntervalValue, 10);
														return !Number.isNaN(n) && (n < QUEUE_INTERVAL_MIN_MIN || n > QUEUE_INTERVAL_MAX_MIN);
													})()}
												/>
												{/* Célula vazia mantém a coluna 2 ocupada — Grid alinha o botão exatamente
												    abaixo do Select da linha acima. */}
												<Box sx={{ display: { xs: 'none', sm: 'block' } }} />
												<Button
													variant="contained"
													color="primary"
													startIcon={<SaveIcon />}
													onClick={handleChangeQueueAlertInterval}
													disabled={(() => {
														const n = parseInt(queueIntervalValue, 10);
														return (
															Number.isNaN(n) ||
															n < QUEUE_INTERVAL_MIN_MIN ||
															n > QUEUE_INTERVAL_MAX_MIN
														);
													})()}
													sx={{ borderRadius: 1.5, boxShadow: 'none', whiteSpace: 'nowrap' }}
												>
													Salvar
												</Button>
											</Box>

											{/* Mensagem de validação FORA do grid — não afeta a altura da linha,
											    portanto o botão fica alinhado com o da linha do threshold. */}
											{(() => {
												const n = parseInt(queueIntervalValue, 10);
												if (Number.isNaN(n)) return null;
												if (n < QUEUE_INTERVAL_MIN_MIN) {
													return (
														<Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'error.main' }}>
															Mínimo {QUEUE_INTERVAL_MIN_MIN} minutos
														</Typography>
													);
												}
												if (n > QUEUE_INTERVAL_MAX_MIN) {
													return (
														<Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'error.main' }}>
															Máximo {QUEUE_INTERVAL_MAX_MIN} minutos
														</Typography>
													);
												}
												return null;
											})()}

											{queueAlertValue !== '' && humanizeQueueAlert(queueAlertValue, queueAlertUnit) && (
												<Box
													sx={{
														mt: 2,
														p: 2,
														borderRadius: 1.5,
														bgcolor: (t) => alpha(t.palette.info.main, 0.06),
														border: (t) => `1px solid ${alpha(t.palette.info.main, 0.25)}`,
													}}
												>
													<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
														<WarningAmberIcon sx={{ fontSize: 18, color: 'info.main' }} />
														<Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
															Como o alerta vai aparecer no grupo
														</Typography>
													</Stack>
													<Box
														sx={{
															fontFamily: 'monospace',
															fontSize: 12,
															lineHeight: 1.5,
															color: 'text.primary',
															whiteSpace: 'pre-wrap',
															bgcolor: 'background.paper',
															p: 1.5,
															borderRadius: 1,
															border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
														}}
													>
														{`⚠️ *Tickets aguardando há mais de ${humanizeQueueAlert(queueAlertValue, queueAlertUnit)} — Suporte*\n🕒 14/05/2026 15:30\n\nHá *3* cliente(s) sem atendimento:\n\n1. *João Silva* — 47min\n2. *Maria Oliveira* — 32min\n3. *Carlos Santos* — 18min`}
													</Box>
													<Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
														Verificação a cada{' '}
														<strong>{humanizeQueueAlert(queueIntervalValue, 'minutes') || '5 minutos'}</strong>{' '}
														· só aparecem clientes que esperam há mais de{' '}
														<strong>{humanizeQueueAlert(queueAlertValue, queueAlertUnit)}</strong> · silenciado entre 23h e 8h (horário SP).
													</Typography>
												</Box>
											)}
										</>
									)}
								</SectionPaper>
							</Box>
						)}
					</Stack>
				</TabPanel>

				{/* ---------------- TAB 2: HORÁRIOS ---------------- */}
				<TabPanel value={activeTab} index={2}>
					<PageHeader
						title={i18n.t("settings.settings.periodTime.title")}
						description="Defina os turnos de trabalho. Usado por automações e respostas de fora do expediente."
					/>

					<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
						<Button
							variant="contained"
							color="primary"
							startIcon={<SaveIcon />}
							onClick={handleChangePeriodOfWork}
							sx={{ borderRadius: 1.5, boxShadow: 'none' }}
						>
							Salvar turnos
						</Button>
					</Box>

					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					) : (
						<Grid container spacing={2.5} alignItems="stretch">
							<Grid item xs={12} md={4}>
								<PeriodBlock icon={LightModeIcon} label="Matutino" period="morning" />
							</Grid>
							<Grid item xs={12} md={4}>
								<PeriodBlock icon={WbTwilightIcon} label="Vespertino" period="afternoon" />
							</Grid>
							<Grid item xs={12} md={4}>
								<PeriodBlock icon={DarkModeIcon} label="Noturno" period="night" />
							</Grid>
						</Grid>
					)}
				</TabPanel>

				{/* ---------------- TAB 3: AVALIAÇÕES ---------------- */}
				<TabPanel value={activeTab} index={3}>
					<PageHeader
						title="Pesquisa de satisfação"
						description="Solicite nota do cliente ao fim do atendimento e personalize a mensagem enviada."
					/>

					<Grid container spacing={2.5} sx={{ mt: 0 }} alignItems="stretch">
						<Grid item xs={12} md={settings?.serviceRating ? 5 : 12}>
							<SwitchSetting
								icon={StarIcon}
								title={i18n.t("settings.settings.serviceRating.name")}
								description="Envia ao cliente um pedido de avaliação assim que o ticket é finalizado."
								tooltip={i18n.t("settings.settings.serviceRating.obs")}
								name="serviceRating"
								checked={settings?.serviceRating}
							/>
						</Grid>

						{settings?.serviceRating && (
							<Grid item xs={12} md={7}>
								<SectionPaper>
									<SectionHeader
										icon={MessageIcon}
										title={
											<Stack direction="row" alignItems="center" spacing={0.75}>
												<span>{i18n.t("settings.settings.messageRating.name")}</span>
												<Tooltip title={i18n.t("settings.settings.messageRating.obs")} arrow>
													<InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
												</Tooltip>
											</Stack>
										}
										description="Mensagem enviada ao cliente solicitando a avaliação do atendimento."
										action={
											<Button
												variant="contained"
												color="primary"
												startIcon={<SaveIcon />}
												onClick={handleChangeMessageRating}
												sx={{ borderRadius: 1.5, boxShadow: 'none' }}
											>
												Salvar
											</Button>
										}
									/>
									{loading ? (
										renderLoading()
									) : (
										<TextField
											multiline
											minRows={4}
											maxRows={10}
											value={messageRating}
											name="messageRating"
											onChange={(e) => setMessageRating(e.target.value)}
											fullWidth
											placeholder="Ex.: Olá! Como você avalia o atendimento que recebeu hoje?"
										/>
									)}
								</SectionPaper>
							</Grid>
						)}
					</Grid>
				</TabPanel>
			</Container>
		</Page>
	);
};

export default Settings;
